import {
  FC, MutableRefObject, useCallback, useMemo, useState,
} from 'react';
import {
  Box, Card, CardHeader, Switch, Typography, useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DataGridPro, GridApiPro, GridColDef, useGridApiRef,
} from '@mui/x-data-grid-pro';
import startCase from 'lodash/startCase';
import moment from 'moment-timezone';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Icon from '@mdi/react';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import { MemoizedBaseAvatar } from '@mgm/common/components/base';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getAttributedOrders, getDataByMemberAndLocation, getIsOrdersLoading } from '../../../../store/orders/selectors';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { TLocation, TMember } from '../../../../types';
import { useTable } from '../../../../hooks/useTable';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { filterOrdersWithLocation } from '../../../../utils/helpers/orders.helper';
import { AnimatedDivider } from '../../../../components';

type TEmployeeSales = {
  members: Array<TMember>
  locationsWithColors: Array<TLocation & { color: string }>;
};

const EmployeeSales: FC<TEmployeeSales> = ({ members, locationsWithColors }) => {
  const orders = useAppSelector(getAttributedOrders);
  const { t } = useTranslation(['orders', 'dashboard', 'common', 'inputs', 'commissions', 'team']);
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const { localText, handleScrollTable } = useTable(apiRef);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const theme = useTheme();
  const { shop } = useAppSelector(getAuthenticateUser);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);
  const dataByMemberAndLocation = useAppSelector(getDataByMemberAndLocation);
  const [fullHeight, setFullHeight] = useState(false);

  const handleHeightToggle = useCallback(() => setFullHeight((prev) => !prev), []);

  const locationNamesById = useMemo(
    () => Object.fromEntries(locationsWithColors.map((location) => [
      location.id,
      { name: location.name, color: location.color },
    ])),
    [locationsWithColors],
  );

  const tableData = useMemo(() => {
    const memberTotals: Record<string, number> = {};
    let dateNetSalesTotal = 0;

    const tableRows = members.flatMap((member) => {
      const memberKey = member.email?.toLowerCase();

      Object.entries(dataByMemberAndLocation[memberKey] || {}).reduce((total, [_, netSales]) => {
        memberTotals[memberKey] = (memberTotals[memberKey] || 0) + netSales;
        return total + netSales;
      }, 0);

      const sortedLocations = Object.entries(dataByMemberAndLocation[memberKey] || {}).sort(([a], [b]) => Number(a) - Number(b));

      return sortedLocations.flatMap(([locationId]) => {
        const dates = Array.from(
          new Set(
            filterOrdersWithLocation(orders, member, locationId)
              .map((order) => moment.tz(order.processedAt, shop?.shopTimezone).format('YYYY-MM-DD')),
          ),
        );

        const locationTotal = filterOrdersWithLocation(orders, member, locationId)
          .reduce((sum, order) => sum + order.summary.netSales, 0);

        return dates.map((date) => {
          const dateNetSales = orders
            .filter(
              (order) => (String(order.userId) === String(member.shopifyId)
                  || (Array.isArray(order.tags)
                    && Array.isArray(member.commissionTags)
                    && order.tags.some((tag) => member.commissionTags.some((commissionTag) => commissionTag.toLowerCase() === tag.toLowerCase()))))
                && order.locationId === Number(locationId)
                && moment.tz(order.processedAt, shop?.shopTimezone).format('YYYY-MM-DD') === date,
            )
            .reduce((sum, order) => sum + order.summary.netSales, 0);

          dateNetSalesTotal += dateNetSales;

          return {
            id: `${member.email}-${locationId}-${date}`,
            name: member.name,
            locationId: `${String(locationId)}-${member.email}` || null,
            locationName: locationNamesById[locationId]?.name || 'Unassigned',
            dateNetSales: dateNetSales || 0,
            color: locationNamesById[locationId]?.color || 'gray',
            dates: date,
            memberTotal: memberTotals[memberKey] || 0,
            locationTotal,
          };
        });
      });
    });

    const totalNetSales = Object.values(memberTotals).reduce((sum, memberTotal) => sum + memberTotal, 0);

    const summaryRow = {
      id: 'summary',
      name: 'Total',
      locationId: null,
      memberTotal: totalNetSales,
      color: 'transparent',
      locationTotal: 0,
      dateNetSales: dateNetSalesTotal,
    };

    return [...tableRows, summaryRow];
  }, [orders, members, locationNamesById, dataByMemberAndLocation]);

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData', { ns: 'common' })} />;

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('name', { ns: 'inputs' }),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={1.5} height="100%">
          {row.id !== 'summary' && (
          <MemoizedBaseAvatar
            statusTooltipText={t('pendingRegistration', { ns: 'team' })}
            status={row.status}
            avatarFile={row.avatarFile}
            avatarName={row.name}
          />
          )}
          <Typography
            {...(row.id === 'summary' && {
              fontWeight: 600,
            })}
            variant="caption"
          >
            {startCase(row.name)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'locationId',
      headerName: t('location', { ns: 'inputs' }),
      minWidth: 300,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%">
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: row.color,
              marginRight: 1,
            }}
          />
          <Typography variant="caption">
            {row.id === 'summary' ? '' : row.locationName || '/'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'dates',
      headerName: t('workingDates'),
      rowSpanValueGetter: () => null,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Typography variant="caption">{row.id === 'summary' ? '' : row.dates || '-'}</Typography>
      ),
    },
    {
      field: 'dateNetSales',
      headerName: `${t('netSales', { ns: 'commissions' })} (${t('date', { ns: 'orders' })})`,
      width: 200,
      rowSpanValueGetter: () => null,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          {row.id !== 'summary'
          && (
          <Typography
            {...(row.id === 'summary' && {
              fontWeight: 600,
            })}
            variant="caption"
          >
            {getCurrency().mark}
            {' '}
            {row.dateNetSales?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'locationTotal',
      headerName: `${t('netSales', { ns: 'commissions' })} (${t('location', { ns: 'inputs' })})`,
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%">
          {row.id !== 'summary' && (
            <Typography
              {...(row.id === 'summary' && {
                fontWeight: 600,
              })}
              variant="caption"
            >
              {getCurrency().mark}
              {' '}
              {row.locationTotal?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'memberTotal',
      headerName: `Total ${t('netSales', { ns: 'commissions' })}`,
      flex: 1,
      minWidth: 200,
      type: 'number',
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%" justifyContent="flex-end">
          <Typography
            {...(row.id === 'summary' && {
              fontWeight: 600,
            })}
            variant="caption"
          >
            {getCurrency().mark}
            {' '}
            {row.memberTotal?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <Card sx={{ height: fullHeight ? 'auto' : 550, pb: 7.5 }}>
      <CardHeader
        sx={{ p: 2 }}
        title={t('salesByEmployee', { ns: 'dashboard' })}
        action={(
          <Stack direction="row" alignItems="center">
            <Box display="flex" alignItems="center">
              <Typography variant="body2">{t('fullTable', { ns: 'orders' })}</Typography>
              <Switch checked={fullHeight} onChange={handleHeightToggle} />
            </Box>
            <Box ml={2} display="flex" alignItems="center">
              <Box onClick={() => handleScrollTable('left')} className="cursor-pointer">
                <Icon color={theme.palette.primary.main} path={mdiChevronLeft} size={1.25} />
              </Box>
              <Box onClick={() => handleScrollTable('right')} className="cursor-pointer">
                <Icon color={theme.palette.primary.main} path={mdiChevronRight} size={1.25} />
              </Box>
            </Box>
          </Stack>
        )}
      />
      <AnimatedDivider loading={isOrdersLoading} />
      {isOrdersLoading ? (
        <Box sx={{ px: 2 }}>
          {[...Array(8)].map((_, idx) => (
            <Skeleton
              key={idx?.toString()}
              variant="rectangular"
              height={40}
              sx={{ mb: 1, borderRadius: 1 }}
              animation="wave"
            />
          ))}
        </Box>
      ) : (
        <DataGridPro
          apiRef={apiRef}
          localeText={localText}
          rows={tableData}
          columns={columns}
          rowSpanning
          showCellVerticalBorder
          showColumnVerticalBorder
          getRowId={(row) => row.id}
          pinnedRows={{
            bottom: [tableData[(tableData?.length || 0) - 1]],
          }}
          slots={{
            noRowsOverlay: renderCustomNoRowsOverlay,
          }}
          hideFooterPagination
          disableRowSelectionOnClick
          hideFooter
        />
      )}
    </Card>
  );
};

export { EmployeeSales };
